import { QuestionPromptCard, type QuestionPictureInfo } from "@/components/QuestionPrompt";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { OptionLabel } from "@/components/OptionLabel";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface MCQMultiProps {
  promptMd: string;
  /** EP-19: the picture under the prompt, if the question has one. */
  picture?: QuestionPictureInfo | null;
  // Server pre-shuffles (per attempt + position) so the candidate JSON
  // never contains a correct-answer signal. Locked down 2026-05-27
  // (pre-ship-audit QA-001).
  options: string[];
  selected: string[];
  onSelect: (next: string[]) => void;
}

export function MCQMulti({
  promptMd,
  picture,
  options,
  selected,
  onSelect,
}: MCQMultiProps) {
  const toggle = (opt: string) => {
    onSelect(
      selected.includes(opt)
        ? selected.filter((s) => s !== opt)
        : [...selected, opt],
    );
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <QuestionPromptCard promptMd={promptMd} picture={picture} />

      <Card>
        <CardHeader>
          <CardTitle>Choose all that apply</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3" role="group" aria-label="Multi-correct options">
            {options.map((opt, idx) => {
              const id = `mcq-multi-opt-${idx}`;
              const isSelected = selected.includes(opt);
              return (
                <Label
                  key={id}
                  htmlFor={id}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-md border p-4 transition-colors",
                    "hover:bg-accent",
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border",
                  )}
                >
                  <input
                    id={id}
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(opt)}
                    className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-base leading-snug">
                    <OptionLabel text={opt} />
                  </span>
                </Label>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default MCQMulti;
